import {parseJson} from "@common/TypesHelper";
import {MethodObserver, SignalRMethod} from "@api/repositories/reatime-repository/types";

type Matcher = (observer: MethodObserver, object: any) => boolean;

/**
 * Matches channels objects.{schemaId}.{lang}, object.{schemaId}.{objectId}.{lang}
 * @param observer
 * @param object
 */
const objectChannelMatcher: Matcher = (observer: MethodObserver, object: any): boolean => {
    return observer.channel.includes(`object.${object.schemaId}.${object.objectId}`)
            || observer.channel.includes(`objects.${object.schemaId}`);
}

/**
 * Matches WebConference.* channels
 * @param observer
 * @param object
 */
const webConferenceMatcher: Matcher = (observer: MethodObserver, object: any): boolean => {
    return observer.channel.indexOf(`WebConference.${object.schemaId}.${object.id}`) >= 0;
}

/**
 * Matches eventMembership.* and eventMemberships.* channels
 * @param observer
 * @param object
 */
const eventMembershipsMatcher: Matcher = (observer: MethodObserver, object: any): boolean => {
    const pattern = 'eventMembership.(\\w+).' + object.agendaId;
    const regExp = new RegExp(pattern)
    return observer.channel.startsWith('eventMemberships.')
        || regExp.test(observer.channel);
}

/**
 * Matches activity channels
 * @param observer
 * @param object
 */
const activityMatcher: Matcher = (observer: MethodObserver, object: any): boolean => {
    return observer.channel.indexOf(`activity.${object.counterId}`) >= 0;
}

/**
 * Matches meeting.* and meetings.* channels
 * @param observer
 * @param object
 */
const meetingsMatcher: Matcher = (observer: MethodObserver, object: any): boolean => {
    return observer.channel.startsWith('meetings.')
        || observer.channel.includes(`meeting.${object.objectId}`);
}

const getMatcher = (method: SignalRMethod): Matcher | undefined => {
    const matchersMap: Record<string, Matcher> = {
        [SignalRMethod.objectUpdated]: objectChannelMatcher,
        [SignalRMethod.objectLikesUpdated]: objectChannelMatcher,
        [SignalRMethod.objectCommentsCountUpdated]: objectChannelMatcher,
        [SignalRMethod.commentsUpdated]: objectChannelMatcher,
        [SignalRMethod.meetingsUpdated]: meetingsMatcher,
        [SignalRMethod.meetingsInvitesUpdated]: meetingsMatcher,
        [SignalRMethod.meetingsCommentsUpdated]: meetingsMatcher,
        [SignalRMethod.webConferencePresentersUpdated]: webConferenceMatcher,
        [SignalRMethod.webConferenceStreamsUpdated]: webConferenceMatcher,
        [SignalRMethod.webConferenceUserHandsUpdate]: webConferenceMatcher,
        [SignalRMethod.activityJoined]: activityMatcher,
        [SignalRMethod.activityLeft]: activityMatcher
    }

    return matchersMap[method];
}

/**
 * Identifies if the channel matches the message
 * If the message comprises schemaId and objectId then the channel should contain 'schemaId.objectId'
 * @param method
 * @param observer
 * @param message
 */
export const isChannelForTheMessage = (method: SignalRMethod, observer: MethodObserver, message: any) => {
    const messageObject: any = parseJson(message);
    const matcher = getMatcher(method);

    if (matcher != undefined) {
        return matcher(observer, messageObject);
    }

    return true;
}
